/** Controlled vocab — keep in sync with AI Feature Plan /01 and backend vocab.js */

export const DEGREE_LEVELS = [
  "bachelor",
  "master",
  "phd",
  "diploma",
  "certificate",
  "foundation",
  "other",
];

export const MODE_OF_STUDY = ["full_time", "part_time", "online", "hybrid"];

export const COVERAGE = ["full", "partial", "other"];

export const FIELD_OF_STUDY = [
  "computer_science",
  "data_science",
  "engineering",
  "business",
  "economics",
  "finance",
  "law",
  "medicine_health",
  "nursing",
  "pharmacy",
  "natural_sciences",
  "mathematics",
  "social_sciences",
  "psychology",
  "arts_humanities",
  "education",
  "architecture",
  "design",
  "media_communications",
  "agriculture",
  "environment",
  "other",
];

export const COUNTRIES = [
  "AE",
  "AT",
  "AU",
  "BE",
  "CA",
  "CH",
  "CN",
  "CY",
  "CZ",
  "DE",
  "DK",
  "ES",
  "FI",
  "FR",
  "GB",
  "GR",
  "HU",
  "IE",
  "IN",
  "IT",
  "JP",
  "KR",
  "MT",
  "NG",
  "NL",
  "NO",
  "NZ",
  "PL",
  "PT",
  "RO",
  "SE",
  "SG",
  "US",
  "ZA",
];

/** ISO alpha-2 → English name. Store the code; show this in the UI. */
export const COUNTRY_NAMES = {
  AE: "United Arab Emirates",
  AR: "Argentina",
  AT: "Austria",
  AU: "Australia",
  BD: "Bangladesh",
  BE: "Belgium",
  BG: "Bulgaria",
  BH: "Bahrain",
  BR: "Brazil",
  CA: "Canada",
  CH: "Switzerland",
  CL: "Chile",
  CN: "China",
  CO: "Colombia",
  CY: "Cyprus",
  CZ: "Czechia",
  DE: "Germany",
  DK: "Denmark",
  EE: "Estonia",
  EG: "Egypt",
  ES: "Spain",
  ET: "Ethiopia",
  FI: "Finland",
  FR: "France",
  GB: "United Kingdom",
  GH: "Ghana",
  GR: "Greece",
  HK: "Hong Kong",
  HR: "Croatia",
  HU: "Hungary",
  ID: "Indonesia",
  IE: "Ireland",
  IL: "Israel",
  IN: "India",
  IS: "Iceland",
  IT: "Italy",
  JP: "Japan",
  KE: "Kenya",
  KR: "South Korea",
  KW: "Kuwait",
  LT: "Lithuania",
  LU: "Luxembourg",
  LV: "Latvia",
  MA: "Morocco",
  MT: "Malta",
  MX: "Mexico",
  MY: "Malaysia",
  NC: "New Caledonia",
  NG: "Nigeria",
  NL: "Netherlands",
  NO: "Norway",
  NZ: "New Zealand",
  OM: "Oman",
  PH: "Philippines",
  PK: "Pakistan",
  PL: "Poland",
  PT: "Portugal",
  QA: "Qatar",
  RO: "Romania",
  RS: "Serbia",
  RU: "Russia",
  RW: "Rwanda",
  SA: "Saudi Arabia",
  SE: "Sweden",
  SG: "Singapore",
  SI: "Slovenia",
  SK: "Slovakia",
  TH: "Thailand",
  TN: "Tunisia",
  TR: "Türkiye",
  TZ: "Tanzania",
  UA: "Ukraine",
  UG: "Uganda",
  US: "United States",
  VN: "Vietnam",
  ZA: "South Africa",
};

/**
 * @param {string|null|undefined} code
 * @return {string}
 */
export function countryLabel(code) {
  if (code == null || code === "") return "";
  const key = String(code).trim().toUpperCase();
  return COUNTRY_NAMES[key] || String(code);
}

export const COUNTRY_OPTIONS = Object.entries(COUNTRY_NAMES)
  .map(([code, name]) => ({ code, name }))
  .sort((a, b) => a.name.localeCompare(b.name));

export const labelize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** Firestore Timestamp-ish → YYYY-MM-DD for date inputs */
export function toDateInputValue(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const d = value.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : "";
  }
  const secs = value._seconds ?? value.seconds;
  if (typeof secs === "number") {
    return new Date(secs * 1000).toISOString().slice(0, 10);
  }
  if (typeof value.toDate === "function") {
    return value.toDate().toISOString().slice(0, 10);
  }
  return "";
}

export function emptySchoolFields() {
  return {
    country: "",
    schoolStructured: {
      location: "",
      universityType: "",
      founded: "",
      enrollment: "",
      acceptanceRate: "",
      ranking: "",
      website: "",
    },
  };
}

export function emptyScholarshipFields() {
  return {
    country: "",
    levels: [],
    amountRaw: "",
    amountCurrency: "",
    amountMinUSD: "",
    amountMaxUSD: "",
    deadlineRaw: "",
    deadlineAt: "",
    isRolling: false,
    coverage: "",
    renewable: null,
    eligibilityText: "",
    applicationUrl: "",
  };
}

export function emptyProgrammeFields() {
  return {
    country: "",
    degreeLevel: "",
    fieldOfStudy: [],
    tuitionRaw: "",
    tuitionCurrency: "",
    tuitionMinUSD: "",
    tuitionMaxUSD: "",
    durationMonths: "",
    modeOfStudy: "",
    languageOfInstruction: "",
    admission: {
      gpaMin: "",
      ielts: "",
      toefl: "",
      gre: "",
      gmat: "",
      otherText: "",
    },
    programmeStructured: {
      department: "",
      faculty: "",
      ects: "",
      curriculumText: "",
    },
  };
}

export function schoolFieldsFromPost(post) {
  const base = emptySchoolFields();
  if (!post) return base;
  const ss = post.schoolStructured || {};
  return {
    country: post.country || "",
    schoolStructured: {
      location: ss.location || post.location || "",
      universityType: ss.universityType || "",
      founded: ss.founded || "",
      enrollment: ss.enrollment || "",
      acceptanceRate: ss.acceptanceRate || "",
      ranking: ss.ranking || "",
      website: ss.website || "",
    },
  };
}

export function scholarshipFieldsFromPost(post) {
  const base = emptyScholarshipFields();
  if (!post) return base;
  return {
    country: post.country || "",
    levels: Array.isArray(post.levels) ? post.levels : [],
    amountRaw: post.amountRaw || "",
    amountCurrency: post.amountCurrency || "",
    amountMinUSD:
      post.amountMinUSD != null && post.amountMinUSD !== ""
        ? String(post.amountMinUSD)
        : "",
    amountMaxUSD:
      post.amountMaxUSD != null && post.amountMaxUSD !== ""
        ? String(post.amountMaxUSD)
        : "",
    deadlineRaw: post.deadlineRaw || "",
    deadlineAt: toDateInputValue(post.deadlineAt),
    isRolling: Boolean(post.isRolling),
    coverage: post.coverage || "",
    renewable: post.renewable == null ? null : Boolean(post.renewable),
    eligibilityText: post.eligibilityText || "",
    applicationUrl: post.applicationUrl || "",
  };
}

export function programmeFieldsFromRecord(rec) {
  const base = emptyProgrammeFields();
  if (!rec) return base;
  const adm = rec.admission || {};
  const ps = rec.programmeStructured || {};
  return {
    country: rec.country || "",
    degreeLevel: rec.degreeLevel || "",
    fieldOfStudy: Array.isArray(rec.fieldOfStudy) ? rec.fieldOfStudy : [],
    tuitionRaw: rec.tuitionRaw || "",
    tuitionCurrency: rec.tuitionCurrency || "",
    tuitionMinUSD:
      rec.tuitionMinUSD != null && rec.tuitionMinUSD !== ""
        ? String(rec.tuitionMinUSD)
        : "",
    tuitionMaxUSD:
      rec.tuitionMaxUSD != null && rec.tuitionMaxUSD !== ""
        ? String(rec.tuitionMaxUSD)
        : "",
    durationMonths:
      rec.durationMonths != null && rec.durationMonths !== ""
        ? String(rec.durationMonths)
        : "",
    modeOfStudy: rec.modeOfStudy || "",
    languageOfInstruction: rec.languageOfInstruction || "",
    admission: {
      gpaMin: adm.gpaMin != null ? String(adm.gpaMin) : "",
      ielts: adm.ielts != null ? String(adm.ielts) : "",
      toefl: adm.toefl != null ? String(adm.toefl) : "",
      gre: adm.gre != null ? String(adm.gre) : "",
      gmat: adm.gmat != null ? String(adm.gmat) : "",
      otherText: adm.otherText || "",
    },
    programmeStructured: {
      department: ps.department || "",
      faculty: ps.faculty || "",
      ects: ps.ects || "",
      curriculumText: ps.curriculumText || "",
    },
  };
}

function numOrNull(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function strOrNull(v) {
  const s = String(v || "").trim();
  return s || null;
}

/** Payload fragment for create/update post APIs */
export function schoolPayloadFromFields(fields) {
  const ss = fields.schoolStructured || {};
  const schoolStructured = {
    ...(strOrNull(ss.location) && { location: strOrNull(ss.location) }),
    ...(strOrNull(ss.universityType) && {
      universityType: strOrNull(ss.universityType),
    }),
    ...(strOrNull(ss.founded) && { founded: strOrNull(ss.founded) }),
    ...(strOrNull(ss.enrollment) && { enrollment: strOrNull(ss.enrollment) }),
    ...(strOrNull(ss.acceptanceRate) && {
      acceptanceRate: strOrNull(ss.acceptanceRate),
    }),
    ...(strOrNull(ss.ranking) && { ranking: strOrNull(ss.ranking) }),
    ...(strOrNull(ss.website) && { website: strOrNull(ss.website) }),
  };
  return {
    ...(strOrNull(fields.country) && { country: strOrNull(fields.country) }),
    ...(Object.keys(schoolStructured).length > 0 && { schoolStructured }),
    ...(strOrNull(ss.location) && { location: strOrNull(ss.location) }),
  };
}

export function scholarshipPayloadFromFields(fields) {
  const payload = {
    ...(strOrNull(fields.country) && { country: strOrNull(fields.country) }),
    ...(Array.isArray(fields.levels) &&
      fields.levels.length > 0 && { levels: fields.levels }),
    ...(strOrNull(fields.amountRaw) && {
      amountRaw: strOrNull(fields.amountRaw),
    }),
    ...(strOrNull(fields.amountCurrency) && {
      amountCurrency: strOrNull(fields.amountCurrency),
    }),
    isRolling: Boolean(fields.isRolling),
    ...(strOrNull(fields.coverage) && { coverage: strOrNull(fields.coverage) }),
    ...(strOrNull(fields.eligibilityText) && {
      eligibilityText: strOrNull(fields.eligibilityText),
    }),
    ...(strOrNull(fields.applicationUrl) && {
      applicationUrl: strOrNull(fields.applicationUrl),
    }),
  };
  const min = numOrNull(fields.amountMinUSD);
  const max = numOrNull(fields.amountMaxUSD);
  if (min != null) payload.amountMinUSD = min;
  if (max != null) payload.amountMaxUSD = max;
  if (fields.renewable === true || fields.renewable === false) {
    payload.renewable = fields.renewable;
  }
  if (fields.isRolling) {
    payload.deadlineAt = null;
  } else if (strOrNull(fields.deadlineAt)) {
    payload.deadlineAt = new Date(`${fields.deadlineAt}T12:00:00.000Z`).toISOString();
  } else if (strOrNull(fields.deadlineRaw)) {
    payload.deadlineRaw = strOrNull(fields.deadlineRaw);
  }
  return payload;
}

export function programmePayloadFromFields(fields) {
  const adm = fields.admission || {};
  const ps = fields.programmeStructured || {};
  const admission = {
    gpaMin: numOrNull(adm.gpaMin),
    ielts: numOrNull(adm.ielts),
    toefl: numOrNull(adm.toefl),
    gre: numOrNull(adm.gre),
    gmat: numOrNull(adm.gmat),
    otherText: strOrNull(adm.otherText),
  };
  const hasAdmission = Object.values(admission).some((v) => v != null);
  const programmeStructured = {
    ...(strOrNull(ps.department) && { department: strOrNull(ps.department) }),
    ...(strOrNull(ps.faculty) && { faculty: strOrNull(ps.faculty) }),
    ...(strOrNull(ps.ects) && { ects: strOrNull(ps.ects) }),
    ...(strOrNull(ps.curriculumText) && {
      curriculumText: strOrNull(ps.curriculumText),
    }),
  };

  const payload = {
    ...(strOrNull(fields.country) && { country: strOrNull(fields.country) }),
    ...(strOrNull(fields.degreeLevel) && {
      degreeLevel: strOrNull(fields.degreeLevel),
    }),
    ...(Array.isArray(fields.fieldOfStudy) &&
      fields.fieldOfStudy.length > 0 && {
        fieldOfStudy: fields.fieldOfStudy,
      }),
    ...(strOrNull(fields.tuitionRaw) && {
      tuitionRaw: strOrNull(fields.tuitionRaw),
    }),
    ...(strOrNull(fields.tuitionCurrency) && {
      tuitionCurrency: strOrNull(fields.tuitionCurrency),
    }),
    ...(strOrNull(fields.modeOfStudy) && {
      modeOfStudy: strOrNull(fields.modeOfStudy),
    }),
    ...(strOrNull(fields.languageOfInstruction) && {
      languageOfInstruction: strOrNull(fields.languageOfInstruction),
    }),
    ...(hasAdmission && { admission }),
    ...(Object.keys(programmeStructured).length > 0 && { programmeStructured }),
  };
  const tMin = numOrNull(fields.tuitionMinUSD);
  const tMax = numOrNull(fields.tuitionMaxUSD);
  const dur = numOrNull(fields.durationMonths);
  if (tMin != null) payload.tuitionMinUSD = tMin;
  if (tMax != null) payload.tuitionMaxUSD = tMax;
  if (dur != null) payload.durationMonths = dur;
  return payload;
}

/** Merge extract-fields API payload into local form state */
export function mergeExtractedIntoSchool(prev, extracted) {
  const ss = extracted.schoolStructured || {};
  return {
    country: extracted.country || prev.country || "",
    schoolStructured: {
      location: ss.location || prev.schoolStructured.location || "",
      universityType:
        ss.universityType || prev.schoolStructured.universityType || "",
      founded: ss.founded || prev.schoolStructured.founded || "",
      enrollment: ss.enrollment || prev.schoolStructured.enrollment || "",
      acceptanceRate:
        ss.acceptanceRate || prev.schoolStructured.acceptanceRate || "",
      ranking: ss.ranking || prev.schoolStructured.ranking || "",
      website: ss.website || prev.schoolStructured.website || "",
    },
  };
}

export function mergeExtractedIntoScholarship(prev, extracted) {
  return {
    ...prev,
    country: extracted.country || prev.country || "",
    levels: Array.isArray(extracted.levels) ? extracted.levels : prev.levels,
    amountRaw: extracted.amountRaw || prev.amountRaw || "",
    amountCurrency: extracted.amountCurrency || prev.amountCurrency || "",
    amountMinUSD:
      extracted.amountMinUSD != null
        ? String(extracted.amountMinUSD)
        : prev.amountMinUSD,
    amountMaxUSD:
      extracted.amountMaxUSD != null
        ? String(extracted.amountMaxUSD)
        : prev.amountMaxUSD,
    deadlineRaw: extracted.deadlineRaw || prev.deadlineRaw || "",
    deadlineAt: toDateInputValue(extracted.deadlineAt) || prev.deadlineAt,
    isRolling:
      extracted.isRolling != null ? Boolean(extracted.isRolling) : prev.isRolling,
    coverage: extracted.coverage || prev.coverage || "",
    renewable:
      extracted.renewable == null ? prev.renewable : Boolean(extracted.renewable),
    eligibilityText: extracted.eligibilityText || prev.eligibilityText || "",
    applicationUrl: extracted.applicationUrl || prev.applicationUrl || "",
  };
}

export function mergeExtractedIntoProgramme(prev, extracted) {
  const adm = extracted.admission || {};
  const ps = extracted.programmeStructured || {};
  return {
    ...prev,
    country: extracted.country || prev.country || "",
    degreeLevel: extracted.degreeLevel || prev.degreeLevel || "",
    fieldOfStudy: Array.isArray(extracted.fieldOfStudy)
      ? extracted.fieldOfStudy
      : prev.fieldOfStudy,
    tuitionRaw: extracted.tuitionRaw || prev.tuitionRaw || "",
    tuitionCurrency: extracted.tuitionCurrency || prev.tuitionCurrency || "",
    tuitionMinUSD:
      extracted.tuitionMinUSD != null
        ? String(extracted.tuitionMinUSD)
        : prev.tuitionMinUSD,
    tuitionMaxUSD:
      extracted.tuitionMaxUSD != null
        ? String(extracted.tuitionMaxUSD)
        : prev.tuitionMaxUSD,
    durationMonths:
      extracted.durationMonths != null
        ? String(extracted.durationMonths)
        : prev.durationMonths,
    modeOfStudy: extracted.modeOfStudy || prev.modeOfStudy || "",
    languageOfInstruction:
      extracted.languageOfInstruction || prev.languageOfInstruction || "",
    admission: {
      gpaMin: adm.gpaMin != null ? String(adm.gpaMin) : prev.admission.gpaMin,
      ielts: adm.ielts != null ? String(adm.ielts) : prev.admission.ielts,
      toefl: adm.toefl != null ? String(adm.toefl) : prev.admission.toefl,
      gre: adm.gre != null ? String(adm.gre) : prev.admission.gre,
      gmat: adm.gmat != null ? String(adm.gmat) : prev.admission.gmat,
      otherText: adm.otherText || prev.admission.otherText || "",
    },
    programmeStructured: {
      department: ps.department || prev.programmeStructured.department || "",
      faculty: ps.faculty || prev.programmeStructured.faculty || "",
      ects: ps.ects || prev.programmeStructured.ects || "",
      curriculumText:
        ps.curriculumText || prev.programmeStructured.curriculumText || "",
    },
  };
}
